import _ from 'lodash';
import { swapMapEntries } from '@/trendData/itemStore.utilities';
import { getDataProps, transformDataPoints } from '@/trendData/seriesData.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { SeriesDataContext } from '@/trendData/seriesData.utilities';
import type { SeriesDataConfig, SeriesRuntimeSample, SeriesValues } from '@/trendData/seriesRuntime.types';
import type { StringEnum } from '@/trendData/trendData.types';

/**
 * In-memory store for the render data a series fetch produces. Like `itemRuntime.store.ts` this is
 * runtime-only and must never be persisted, but it is series-specific rather than shared across
 * item types, so it lives in its own store.
 */

/** The subset of `getDataProps` output this store owns */
const RUNTIME_FIELDS = [
  'data',
  'samples',
  'zones',
  'capsuleSegmentSamples',
  'capsuleSegmentData',
  'isStringSeries',
  'stringEnum',
  'calculatedStringEnum',
  'icon',
] as const;

export interface SeriesRuntimeState {
  /** Render data keyed by item id; series with no entry have not received results */
  values: Partial<Record<string, SeriesValues>>;
}

export interface SeriesRuntimeActions {
  /**
   * Derives and records a series' render data. Takes the fetch results plus the item configuration
   * the transforms depend on, so the derivation lives here and no caller can store unprocessed data.
   */
  setData(
    id: string,
    params: {
      payload: {
        samples: SeriesRuntimeSample[];
        valueUnitOfMeasure: string;
        interpolationMethod?: string;
        stringEnum?: StringEnum[];
        capsuleSegmentSamples?: SeriesRuntimeSample[];
      };
      config: SeriesDataConfig;
      context: SeriesDataContext;
    },
  ): void;
  /**
   * Re-derives an item's render data from the samples it already holds, for when the configuration
   * feeding the transforms changes rather than the data itself.
   */
  rebuildData(id: string, params: { config: SeriesDataConfig; context: SeriesDataContext }): void;
  /**
   * Recolors the zone marking uncertainty, which is drawn in a translucent item color; an undefined
   * color clears it, matching the item's color being cleared
   */
  setZoneColor(id: string, color: string | undefined): void;
  /**
   * Empties an item's drawable data while keeping the entry, matching a terminal-status clear.
   * TODO CRAB-57427: rename to `clearDataForTerminalStatus` as part of unifying the fetched-data
   * store surface, so the dispatch sites can loop instead of special-casing this store.
   */
  clearData(id: string): void;
  /** Discards an item's render data entirely, for when the item no longer exists */
  clearValues(id: string): void;
  /**
   * Moves entries to their swapped-in ids, mirroring the flux stores renaming items in place on
   * TREND_SWAP_ITEMS: the swapped-in item keeps rendering the previous render data until its
   * fetch returns, and nothing is left keyed by an id that no longer exists.
   */
  swapValues(swaps: Record<string, string>): void;
  reset(): void;
}

export const initialState: SeriesRuntimeState = Object.freeze({
  values: {},
});

const STORE_NAME = 'SeriesRuntime';

const seriesRuntimeActionsFactory: ActionsFactory<SeriesRuntimeState, SeriesRuntimeActions> = (
  set,
  get,
  resetState,
) => ({
  setData: (id, { payload, config, context }) => {
    const current = get().values[id];
    const derived = getDataProps({
      payload: {
        ...payload,
        samples: payload.samples,
        interpolationMethod: payload.interpolationMethod,
        color: config.color,
        lineWidth: config.lineWidth,
        capsuleSegmentSamples: (payload.capsuleSegmentSamples ??
          current?.capsuleSegmentSamples) as SeriesRuntimeSample[],
      },
      sampleDisplayOption: config.sampleDisplayOption,
      minTime: config.minTime,
      hasBounds: config.hasBounds,
      context,
    });

    const update = _.pick(derived as Record<string, unknown>, [...RUNTIME_FIELDS]) as SeriesValues;
    const merged = { ...current, ...update };
    if (_.isEqual(current, merged)) {
      return;
    }
    set((draft) => {
      draft.values[id] = merged;
    });
  },

  rebuildData: (id, { config, context }) => {
    const current = get().values[id];
    if (!current) {
      return;
    }

    const update = transformDataPoints({
      rawSamples: current.samples ?? [],
      color: config.color,
      lineWidth: config.lineWidth,
      minTime: config.minTime,
      sampleDisplayOption: config.sampleDisplayOption,
      hasBounds: config.hasBounds,
      stringEnum: current.stringEnum,
      ...context,
    });

    const merged: SeriesValues = {
      ...current,
      ...(_.pick(update as Record<string, unknown>, [...RUNTIME_FIELDS]) as SeriesValues),
    };
    if (_.isEqual(current, merged)) {
      return;
    }
    set((draft) => {
      draft.values[id] = merged;
    });
  },

  setZoneColor: (id, color) => {
    const zones = get().values[id]?.zones;
    if (!zones?.some((zone) => 'color' in zone)) {
      return;
    }
    set((draft) => {
      const entry = draft.values[id];
      if (entry) {
        entry.zones = zones.map((zone) => ('color' in zone ? { ...zone, color } : zone));
      }
    });
  },

  clearData: (id) => {
    if (_.isEmpty(get().values[id]?.data)) {
      return;
    }
    set((draft) => {
      const entry = draft.values[id];
      if (entry) {
        entry.data = [];
      }
    });
  },

  clearValues: (id) => {
    if (!(id in get().values)) {
      return;
    }
    set((draft) => {
      delete draft.values[id];
    });
  },

  swapValues: (swaps) => {
    if (!Object.keys(swaps).some((swappedOutId) => swappedOutId in get().values)) {
      return;
    }
    set((draft) => {
      swapMapEntries(draft.values, swaps);
    });
  },

  reset: resetState,
});

const [SeriesRuntimeStoreProvider, useSeriesRuntimeStoreInstance, useSeriesRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  seriesRuntimeActionsFactory,
);

export { SeriesRuntimeStoreProvider };
export type { SeriesDataConfig, SeriesValues } from '@/trendData/seriesRuntime.types';

export const useSeriesValues = () => useSeriesRuntimeSelector((state) => state.values);

export const useSeriesValue = (id: string): SeriesValues | undefined =>
  useSeriesRuntimeSelector((state) => state.values[id]);

export const useSeriesRuntimeActions = () => useSeriesRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateSeriesRuntimeStore(initialStateOverrides?: Partial<SeriesRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    seriesRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
